import { useBoardStore } from '@flowwai-frontend/shared/sales-crm/sales-crm-core'
import { useGetPipelineCallsDetails } from '@flowwai-frontend/bps-officer-web/calls'
import {
   GENERATE_SCRUTINY_KEY_ROUTE_PATH,
   TDR_MANAGEMENT_ROUTE_PATH
} from '@flowwai-frontend/bps-officer-web/common/data-access'
import { SEARCH_AND_MONITORING_ROUTE_PATH } from '@flowwai-frontend/bps-officer-web/common/data-access'
import {
   isAnalyticsCoPilotWebEnabled,
   isAssignFriendEnabled,
   isGISTabEnabled,
   isNotificationsWebEnabled,
   isSearchAndMonitoringEnabled,
   enableTDRLegacyApplications
} from '@flowwai-frontend/bps-officer-web/common/ui'
import { Config } from '@flowwai-frontend/bps-officer-web/env-config'
import {
   useGetLegacyApplicationsFeatureToggle,
   useIsDashboardAdmin,
   useUserAppConfigStore
} from '@flowwai-frontend/bps-officer-web/iam'
import { ApplicationNavItem } from '@flowwai-frontend/bps-officer-web/navbar-base'
import {
   PMU_USERS_LOGS_ROUTE_PATH,
   PMU_USERS_ROUTE_PATH,
   LTP_USERS_ROUTE_PATH,
   LTP_USERS_LOGS_ROUTE_PATH,
   PMU_APPLICATION_MANAGEMENT_ROUTE_PATH,
   PMU_APPLICATION_MANAGEMENT_LOGS_ROUTE_PATH,
   ON_HOLD_TDR_CERTIFICATE_ROUTE_PATH,
   CERTIFICATES_LOGS_ROUTE_PATH,
   RELEASE_CERTIFICATE_ROUTE_PATH,
   TASK_SHORTFALL_ROUTE_PATH,
   APPLICATION_MANAGEMENT_LOGS_ROUTE_PATH,
   NEW_USER_NEW_OFFICER_ROUTE_PATH
} from '@flowwai-frontend/bps-officer-web/common/data-access'
import { useCustomT } from '@flowwai-frontend/shared/common-components'
import { isGenerateScrutinyKeyEnabled } from '@flowwai-frontend/shared-web/common/ui'
import { useGetFeatureGates } from '@flowwai-frontend/shared-web/feature-toggles'
import {
   LEGACY_APPLICATIONS_ROUTE_PATH,
   TDR_LEGACY_APPLICATIONS_ROUTE_PATH
} from '@flowwai-frontend/bps-officer-web/common/data-access'
import { useGetSearchAndMonitoringConfigCache } from '@flowwai-frontend/bps-officer-web/search-and-monitoring/data-access'

import {
   ANALYTICS,
   ANALYTICS_CO_PILOT,
   APPLICATIONS_ASSIGNED_BY_FRIEND,
   CALLS,
   CONTACTS,
   DASHBOARDS,
   GENERATE_SCRUTINY,
   GIS,
   HOME,
   NOTIFICATIONS,
   PINNED,
   USERS,
   USERS_LOGS,
   NEW_USER_NEW_OFFICER,
   LTP_USERS,
   LTP_USERS_LOGS,
   LEGACY_APPLICATIONS,
   SEARCH_AND_MONITORING,
   PMU_APPLICATION_MANAGEMENT,
   PMU_APPLICATIONS,
   PMU_APPLICATION_MANAGEMENT_LOGS,
   TDR_LEGACY_APPLICATIONS,
   ON_HOLD_TDR_CERTIFICATE,
   CERTIFICATES_LOGS,
   RELEASE_CERTIFICATE,
   TDR_MANAGEMENT,
   TASK_SHORTFALL,
   APPLICATION_MANAGEMENT_LOGS
} from '../../constants/NavItemConstants'
import { isPinnedBoardsEnabled } from '../../utils/FeatureToggles'

const useGetApplicationNavItemsData = (): ApplicationNavItem[] => {
   const t = useCustomT('navbar:applicationNavItems')
   const boardStore = useBoardStore()
   const isDashboardAdmin = useIsDashboardAdmin()

   const { permissions } = useUserAppConfigStore()

   const { filterFields } = useGetSearchAndMonitoringConfigCache()

   const {
      isLegacyApplicationsEnabled
   } = useGetLegacyApplicationsFeatureToggle()

   const isSearchAndMonitoringRouteEnabled =
      isSearchAndMonitoringEnabled && filterFields.length > 0

   const contactBoardId = boardStore.firstContactBoard?.boardId

   const isContactsEnabled = permissions?.isContactsEnabled ?? true

   const isSchedulesEnabled =
      (permissions?.isSchedulingEnabled &&
         Config.ENABLE_HOME_BOARD_ROUTE === 'true') ??
      false

   const isDataCoPilotEnabled =
      !!permissions?.isDataCoPilotEnabled && isAnalyticsCoPilotWebEnabled

   const isPMUOfficer = permissions?.userRole === 'PMU_OFFICER'

   const isApplicationManagementEnabled =
      permissions?.isApplicationManagementEnabled

   const isTdrManagementEnabled = permissions?.isTdrManagementEnabled

   const isLtpUserManagementEnabled = permissions?.isLtpUserManagementEnabled

   const isScrutinyKeyGenerationEnabled = !!permissions?.isScrutinyKeyGenerationEnabled

   const { isCallsEnabled } = useGetPipelineCallsDetails()

   const { isNewUserNewOfficerEnabled } = useGetFeatureGates()

   const applicationHomeConfig = isSchedulesEnabled
      ? [
           {
              id: HOME,
              name: t('home'),
              iconId: 'home-line',
              slug: '/home'
           }
        ]
      : []

   const applicationConfig: ApplicationNavItem[] = [
      ...applicationHomeConfig,

      {
         id: DASHBOARDS,
         name: t('dashboards'),
         iconId: 'layout-alt-04',
         slug: '/dashboards'
      }
   ]

   if (isAssignFriendEnabled) {
      applicationConfig.push({
         id: APPLICATIONS_ASSIGNED_BY_FRIEND,
         name: t('applicationsAssignedByFriend'),
         iconId: 'face-smile',
         slug: '/assigned-by-friend'
      })
   }

   if (isPinnedBoardsEnabled) {
      applicationConfig.push({
         id: PINNED,
         name: t('pinned'),
         iconId: 'pin-01',
         slug: '/pinned'
      })
   }

   if (isScrutinyKeyGenerationEnabled) {
      applicationConfig.push({
         id: GENERATE_SCRUTINY,
         name: t('generateScrutiny'),
         iconId: 'lock-key-hole',
         slug: GENERATE_SCRUTINY_KEY_ROUTE_PATH
      })
   }

   //  if (isCallsEnabled) {
   //     applicationConfig.push({
   //        id: CALLS,
   //        name: t('calls'),
   //        iconId: 'phone',
   //        slug: '/calls'
   //     })
   //  }

   //  if (isContactsEnabled) {
   //     applicationConfig.push({
   //        id: CONTACTS,
   //        name: t('contacts'),
   //        iconId: 'user-circle',
   //        slug: `/contact-board/${contactBoardId}`
   //     })
   //  }

   if (isNotificationsWebEnabled) {
      applicationConfig.push({
         id: NOTIFICATIONS,
         name: t('notifications'),
         iconId: 'bell-03',
         slug: '/notifications'
      })
   }

   if (isDataCoPilotEnabled) {
      applicationConfig.push({
         id: ANALYTICS_CO_PILOT,
         name: t('analyticsCoPilot'),
         iconId: 'analytics-co-pilot',
         slug: '/analytics-copilot'
      })
   }

   if (isGISTabEnabled) {
      applicationConfig.push({
         id: GIS,
         name: t('gis'),
         iconId: 'gis',
         slug: '/gis-embed-url'
      })
   }

   if (isPMUOfficer) {
      applicationConfig.push({
         id: USERS,
         name: t('users'),
         iconId: 'user-roles',
         slug: PMU_USERS_ROUTE_PATH
      })
      applicationConfig.push({
         id: USERS_LOGS,
         name: t('usersLogs'),
         iconId: 'template',
         slug: PMU_USERS_LOGS_ROUTE_PATH
      })
      if (isNewUserNewOfficerEnabled) {
         applicationConfig.push({
            id: NEW_USER_NEW_OFFICER,
            name: t('newOfficer'),
            iconId: 'user-plus-01',
            slug: NEW_USER_NEW_OFFICER_ROUTE_PATH
         })
      }
   }

   if (isLtpUserManagementEnabled) {
      applicationConfig.push({
         id: LTP_USERS,
         name: t('ltpUsers'),
         iconId: 'user-roles',
         slug: LTP_USERS_ROUTE_PATH
      })
      applicationConfig.push({
         id: LTP_USERS_LOGS,
         name: t('ltpUsersLogs'),
         iconId: 'template',
         slug: LTP_USERS_LOGS_ROUTE_PATH
      })
   }

   const tdrManagementConfig: ApplicationNavItem[] = [
      {
         id: TDR_MANAGEMENT,
         name: t('tdrBank'),
         iconId: 'certificate-01',
         slug: TDR_MANAGEMENT_ROUTE_PATH,
         children: [
            {
               id: ON_HOLD_TDR_CERTIFICATE,
               name: t('onHoldTdrCertificate'),
               iconId: 'hand',
               slug: ON_HOLD_TDR_CERTIFICATE_ROUTE_PATH
            },
            {
               id: RELEASE_CERTIFICATE,
               name: t('releaseCertificate'),
               iconId: 'rocket-01',
               slug: RELEASE_CERTIFICATE_ROUTE_PATH
            },
            {
               id: CERTIFICATES_LOGS,
               name: t('certificatesLogs'),
               iconId: 'document-01',
               slug: CERTIFICATES_LOGS_ROUTE_PATH
            }
         ]
      }
   ]

   if (isTdrManagementEnabled) {
      applicationConfig.push(...tdrManagementConfig)
   }

   const applicationManagementConfig: ApplicationNavItem[] = [
      {
         id: PMU_APPLICATION_MANAGEMENT,
         name: t('applicationManagement'),
         iconId: 'file-settings',
         slug: PMU_APPLICATION_MANAGEMENT_ROUTE_PATH,
         children: [
            {
               id: PMU_APPLICATIONS,
               name: t('applications'),
               iconId: 'document-check',
               slug: PMU_APPLICATION_MANAGEMENT_ROUTE_PATH
            },
            {
               id: PMU_APPLICATION_MANAGEMENT_LOGS,
               name: t('applicationManagementLogs'),
               iconId: 'file-02',
               slug: PMU_APPLICATION_MANAGEMENT_LOGS_ROUTE_PATH
            },
            {
               id: TASK_SHORTFALL,
               name: t('taskShortfall'),
               iconId: 'arrow-circle-broken-down-left',
               slug: TASK_SHORTFALL_ROUTE_PATH
            },
            {
               id: APPLICATION_MANAGEMENT_LOGS,
               name: t('activityLogs'),
               iconId: 'file-02',
               slug: APPLICATION_MANAGEMENT_LOGS_ROUTE_PATH
            }
         ]
      }
   ]

   if (isApplicationManagementEnabled) {
      applicationConfig.push(...applicationManagementConfig)
   }

   if (isLegacyApplicationsEnabled) {
      applicationConfig.push({
         id: LEGACY_APPLICATIONS,
         name: t('tgBpass'),
         iconId: 'document-01',
         slug: LEGACY_APPLICATIONS_ROUTE_PATH
      })
   }

   if (enableTDRLegacyApplications) {
      applicationConfig.push({
         id: TDR_LEGACY_APPLICATIONS,
         name: t('tdrLegacyApplications'),
         iconId: 'document-01',
         slug: TDR_LEGACY_APPLICATIONS_ROUTE_PATH
      })
   }

   if (isSearchAndMonitoringRouteEnabled) {
      applicationConfig.push({
         id: SEARCH_AND_MONITORING,
         name: t('searchAndMonitoring'),
         iconId: 'search-md',
         slug: SEARCH_AND_MONITORING_ROUTE_PATH
      })
   }

   // For dashboard admin users, only show DASHBOARDS route
   if (isDashboardAdmin) {
      return applicationConfig.filter(item => item.id === DASHBOARDS)
   }

   return applicationConfig
}

export default useGetApplicationNavItemsData
